import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { getEbitda, putEbitdaAjustes, getEjercicios } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Save, ChevronDown, ChevronRight, Pencil, Plus, Trash2, ArrowUpRight } from "lucide-react";
import EbitdaModoSwitch, { useEbitdaModo } from "./EbitdaModoSwitch";

// Impacto en el EBITDA de un ajuste, dado el valor_pyg del target (espejo del backend).
function impactoDe(a, valor) {
  if (a.scope === "libre") return Number(a.importe) || 0;
  const v = valor || 0;
  let aplicado;
  if (a.tipo === "total") aplicado = v;
  else if (a.tipo === "porcentaje") aplicado = (v * (Number(a.porcentaje) || 0)) / 100;
  else aplicado = Math.sign(v || 1) * Math.abs(Number(a.importe) || 0);
  return a.direccion === "excluir" ? -aplicado : aplicado;
}

// Impacto REAL de un ajuste con las dos reglas de `domain/ebitda.py::resolver`
// (revisión de dependencias, nov-2026: antes la pantalla no las aplicaba y su
// EBITDA ajustado podía no coincidir con el del dashboard):
//  - anti doble conteo: un ajuste de CUENTA dentro de una partida que ya tiene
//    ajuste de partida entera se ignora (impacto 0);
//  - F6: "excluir" sobre una partida que ya está FUERA del EBITDA base
//    (9/11/12) se neutraliza (impacto 0) — restaría dos veces.
function impactoEfectivo(a, valor, reglas) {
  if (a.scope === "libre") return impactoDe(a, valor);
  const partida = a.scope === "partida" ? a.target : reglas.cuentaAPartida[a.target];
  if (a.scope === "cuenta" && reglas.partidasAjustadas.has(partida)) return 0;
  if (a.direccion === "excluir" && reglas.partidasFuera.has(partida)) return 0;
  return impactoDe(a, valor);
}

// Etiqueta corta del ajuste para mostrarla bajo la línea.
function resumenAjuste(a) {
  if (a.scope === "libre") return "ajuste libre";
  const partes = [a.direccion];
  if (a.tipo === "porcentaje") partes.push(`${a.porcentaje ?? 0}%`);
  else if (a.tipo === "importe") partes.push("importe");
  else partes.push("total");
  return partes.join(" · ");
}

export default function EbitdaContent({ ejercicioId, ejercicio }) {
  const empresaId = ejercicio?.empresa_id;
  const [modo, setModo] = useEbitdaModo(empresaId);
  const [data, setData] = useState(null);
  const [ajustes, setAjustes] = useState([]); // ajustes propios del año (editables)
  const [heredados, setHeredados] = useState([]); // de otros años (aplicar_en), solo lectura
  const [aniosOtros, setAniosOtros] = useState([]); // años de la empresa distintos al actual
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});

  const anioActual = ejercicio?.anio;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEbitda(ejercicioId, modo);
      setData(res.data);
      const all = res.data.ajustes || [];
      setAjustes(all.filter((a) => a.origen !== "heredado"));
      setHeredados(all.filter((a) => a.origen === "heredado"));
    } catch (e) {
      toast.error("Error al cargar EBITDA");
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [ejercicioId, modo]);

  useEffect(() => {
    load();
  }, [load]);

  // Años de la empresa (para el selector "aplicar a varios años").
  useEffect(() => {
    if (!empresaId) return;
    getEjercicios(empresaId)
      .then((res) => {
        const anios = (res.data || [])
          .map((e) => e.anio)
          .filter((a) => a && a !== anioActual)
          .sort((a, b) => a - b);
        setAniosOtros(anios);
      })
      .catch(() => setAniosOtros([]));
  }, [empresaId, anioActual]);

  // Índice valor_pyg por target (cuenta y partida) desde el catálogo.
  const valorByTarget = useMemo(() => {
    const m = {};
    (data?.catalogo || []).forEach((p) => {
      m[p.nombre] = p.valor_pyg;
      (p.cuentas || []).forEach((c) => (m[c.codigo] = c.valor_pyg));
    });
    return m;
  }, [data]);

  const editable = modo !== "base";

  // Índice cuenta → nombre de su partida (para "ir a la línea" desde el desglose).
  const cuentaAPartida = useMemo(() => {
    const m = {};
    (data?.catalogo || []).forEach((p) =>
      (p.cuentas || []).forEach((c) => (m[c.codigo] = p.nombre))
    );
    return m;
  }, [data]);

  // Reglas del backend para el impacto efectivo (ver impactoEfectivo).
  const reglas = useMemo(
    () => ({
      cuentaAPartida,
      partidasFuera: new Set((data?.catalogo || []).filter((p) => !p.en_ebitda).map((p) => p.nombre)),
      partidasAjustadas: new Set(
        [...ajustes, ...heredados].filter((a) => a.scope === "partida").map((a) => a.target)
      ),
    }),
    [cuentaAPartida, data, ajustes, heredados]
  );

  // Ajuste asociado a un target concreto (cuenta o partida), con su impacto
  // efectivo ya calculado (`_impacto`).
  const ajusteDe = useCallback(
    (target) => {
      const i = ajustes.findIndex((a) => a.scope !== "libre" && a.target === target);
      if (i < 0) return null;
      const a = ajustes[i];
      return { ...a, _i: i, _impacto: impactoEfectivo(a, valorByTarget[a.target], reglas) };
    },
    [ajustes, valorByTarget, reglas]
  );

  const libres = useMemo(
    () => ajustes.map((a, i) => ({ a, i })).filter(({ a }) => a.scope === "libre"),
    [ajustes]
  );

  // Desglose consolidado de TODOS los ajustes aplicados (solo lectura).
  const desglose = useMemo(
    () =>
      ajustes.map((a, i) => {
        const valor = a.scope === "libre" ? null : valorByTarget[a.target] ?? 0;
        return {
          a,
          i,
          valor,
          impacto: impactoEfectivo(a, valor, reglas),
          etiqueta: a.scope === "libre" ? "Ajuste libre" : a.target,
        };
      }),
    [ajustes, valorByTarget, reglas]
  );

  const [desgloseOpen, setDesgloseOpen] = useState(false);
  const [highlight, setHighlight] = useState(null);

  // Lleva el foco a la línea del informe donde vive el ajuste y la resalta.
  const irALinea = useCallback(
    (a) => {
      if (a.scope === "libre") {
        document
          .querySelector("[data-ebitda-libres]")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        const partida = a.scope === "partida" ? a.target : cuentaAPartida[a.target];
        if (partida) setExpanded((s) => ({ ...s, [partida]: true }));
        setHighlight(a.target);
        setTimeout(() => {
          const esc = window.CSS && CSS.escape ? CSS.escape(a.target) : a.target;
          document
            .querySelector(`[data-ebitda-target="${esc}"]`)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 60);
        setTimeout(() => setHighlight(null), 2200);
      }
    },
    [cuentaAPartida]
  );

  const base = data?.ebitda_base || 0;
  const re = data?.resultado_explotacion || 0;
  const amort = data?.amortizaciones || 0;
  const ventas = useMemo(() => {
    const p1 = (data?.catalogo || []).find((p) => p.numero === "1");
    return p1?.valor_pyg || 0;
  }, [data]);

  const totalPropios = ajustes.reduce((s, a) => s + impactoEfectivo(a, valorByTarget[a.target], reglas), 0);
  // Un ajuste propio del año sobre el mismo target gana al heredado (como en el backend).
  // Y un heredado de CUENTA no suma si su partida ya tiene ajuste de partida
  // (propio, aunque aún no esté guardado): anti doble conteo del backend.
  const targetsPropios = new Set(ajustes.filter((a) => a.scope !== "libre").map((a) => a.target));
  const partidasPropias = new Set(ajustes.filter((a) => a.scope === "partida").map((a) => a.target));
  const totalHeredados = heredados
    .filter((a) => a.scope === "libre" || !targetsPropios.has(a.target))
    .filter((a) => !(a.scope === "cuenta" && partidasPropias.has(cuentaAPartida[a.target])))
    .reduce((s, a) => s + (Number(a.impacto) || 0), 0);
  const totalAjustes = totalPropios + totalHeredados;
  const ebitdaAjustado = base + totalAjustes;
  const margenBase = ventas > 0 ? (base / ventas) * 100 : null;
  const margenAj = ventas > 0 ? (ebitdaAjustado / ventas) * 100 : null;

  // ── Mutaciones de ajustes ──────────────────────────────────────────────
  const addAjuste = (scope, target, enEbitda) => {
    if (!editable) return;
    setAjustes((prev) => [
      ...prev,
      {
        scope,
        target,
        direccion: enEbitda ? "excluir" : "incluir",
        tipo: "total",
        importe: scope === "libre" ? 0 : null,
        porcentaje: null,
        propagar: false,
        aplicar_en: null,
        concepto: "otro",
        nota: "",
      },
    ]);
  };

  const updateAjuste = (i, patch) =>
    setAjustes((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  const removeAjuste = (i) => setAjustes((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = ajustes.map((a) => ({
        scope: a.scope,
        target: a.scope === "libre" ? null : a.target,
        direccion: a.direccion,
        tipo: a.tipo,
        importe: a.importe === "" ? null : a.importe,
        porcentaje: a.porcentaje === "" ? null : a.porcentaje,
        propagar: false,
        aplicar_en: Array.isArray(a.aplicar_en) && a.aplicar_en.length ? a.aplicar_en : null,
        concepto: a.concepto || "otro",
        nota: a.nota || "",
      }));
      await putEbitdaAjustes(ejercicioId, payload);
      toast.success("Ajustes de EBITDA guardados");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar ajustes");
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const enEbitda = (data?.catalogo || []).filter((p) => p.en_ebitda);
  const fuera = (data?.catalogo || []).filter((p) => !p.en_ebitda);

  return (
    <div className="space-y-4" data-testid="ebitda-content">
      {/* Cabecera: switch + guardar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <EbitdaModoSwitch modo={modo} onChange={setModo} />
          <Badge variant="outline" className="text-xs">
            {modo === "base" ? "EBITDA base" : "Ajustado (año)"}
          </Badge>
        </div>
        <Button onClick={handleSave} disabled={saving || !editable} data-testid="save-config-button">
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Guardando..." : "Guardar ajustes"}
        </Button>
      </div>

      {/* Informe en formato cuenta de resultados */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Normalización del EBITDA</CardTitle>
          <CardDescription>
            {editable
              ? "Ajusta cualquier partida o cuenta en su línea (total, % o importe). El resultado se ve en formato informe."
              : "Vista del EBITDA base sin ajustes. Cambia a «Ajustado» para normalizar."}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Cabeceras de columna */}
          <div className="grid grid-cols-12 gap-2 px-3 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
            <span className="col-span-6">Concepto</span>
            <span className="col-span-3 text-right">Valor PyG</span>
            <span className="col-span-3 text-right">{editable ? "Ajuste EBITDA" : ""}</span>
          </div>

          {/* Bloque base */}
          <FilaSimple label="Resultado de Explotación (A.1)" valor={re} editable={editable} />
          <FilaSimple label="+ Amortizaciones (Partida 8)" valor={amort} editable={editable} />
          <FilaTotal
            label="= EBITDA BASE"
            valor={base}
            margen={margenBase}
            tone="blue"
          />

          {/* Catálogo de partidas como estado */}
          <div className="mt-3 space-y-1">
            <GrupoPartidas
              titulo="Explotación (forma el EBITDA)"
              partidas={enEbitda}
              {...{ expanded, setExpanded, editable, ajusteDe, addAjuste, updateAjuste, removeAjuste, valorByTarget, highlight, aniosOtros }}
            />
            <GrupoPartidas
              titulo="Fuera del EBITDA (financiero · impuestos · amortización)"
              partidas={fuera}
              {...{ expanded, setExpanded, editable, ajusteDe, addAjuste, updateAjuste, removeAjuste, valorByTarget, highlight, aniosOtros }}
            />
          </div>

          {/* Otras normalizaciones (ajustes libres) */}
          {editable && (
            <div className="mt-4" data-ebitda-libres>
              <div className="flex items-center justify-between px-3 mb-1">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Otras normalizaciones (ajuste libre)
                </h4>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addAjuste("libre", null, false)}>
                  <Plus className="h-3 w-3 mr-1" /> Añadir
                </Button>
              </div>
              {libres.length === 0 ? (
                <p className="text-xs text-muted-foreground px-3 py-1">
                  Importes de normalización sin una cuenta concreta detrás.
                </p>
              ) : (
                libres.map(({ a, i }) => (
                  <FilaLibre
                    key={i}
                    a={a}
                    impacto={impactoDe(a, null)}
                    onChange={(patch) => updateAjuste(i, patch)}
                    onRemove={() => removeAjuste(i)}
                  />
                ))
              )}
            </div>
          )}

          {/* Ajustes heredados de otros años (solo lectura, vía "aplicar a varios años") */}
          {editable && heredados.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 mb-1">
                Heredados de otros años
              </h4>
              {heredados.map((a, i) => (
                <div key={a.id || i} className="grid grid-cols-12 gap-2 items-center py-1 px-3 text-xs">
                  <span className="col-span-6 truncate text-muted-foreground">
                    {(a.nota || "").trim() || a.concepto || a.target} ·{" "}
                    <span className="font-mono">{a.scope === "libre" ? "libre" : a.target}</span>
                  </span>
                  <span className="col-span-3 text-right text-[11px] text-muted-foreground">
                    {resumenAjuste(a)}
                  </span>
                  <span className={`col-span-3 text-right font-mono ${(a.impacto || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {(a.impacto || 0) >= 0 ? "+" : ""}{formatCurrency(a.impacto || 0)}
                  </span>
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground px-3 mt-0.5">
                Definidos en otro año y aplicados aquí. Se editan desde su año de origen.
              </p>
            </div>
          )}

          <Separator className="my-3" />

          {/* Total ajustes (desplegable con desglose) + EBITDA ajustado */}
          {editable && (
            <DesgloseAjustes
              filas={desglose}
              open={desgloseOpen}
              onToggle={setDesgloseOpen}
              totalAjustes={totalAjustes}
              onGoto={irALinea}
            />
          )}
          <FilaTotal
            label="= EBITDA AJUSTADO"
            valor={ebitdaAjustado}
            margen={margenAj}
            tone="primary"
            delta={margenBase != null && margenAj != null ? margenAj - margenBase : null}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ── Filas del informe ─────────────────────────────────────────────────────

function FilaSimple({ label, valor, editable, mutedLabel }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center py-1.5 px-3">
      <span className={`col-span-6 text-sm ${mutedLabel ? "text-muted-foreground" : ""}`}>{label}</span>
      <span className={`col-span-3 text-right font-mono text-sm ${valor < 0 ? "text-rose-600" : ""}`}>
        {formatCurrency(valor)}
      </span>
      <span className="col-span-3" />
    </div>
  );
}

function FilaTotal({ label, valor, margen, tone, delta }) {
  const bg =
    tone === "primary"
      ? "bg-primary text-primary-foreground"
      : "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300";
  return (
    <div className={`grid grid-cols-12 gap-2 items-center py-2.5 px-3 rounded-lg my-1 ${bg}`}>
      <span className="col-span-6 font-bold text-sm">{label}</span>
      <span className="col-span-3 text-right font-mono font-bold">{formatCurrency(valor)}</span>
      <span className="col-span-3 text-right text-xs">
        {margen != null && <>margen {margen.toFixed(1)}%</>}
        {delta != null && (
          <span className="block opacity-80">Δ {delta >= 0 ? "+" : ""}{delta.toFixed(1)} pp</span>
        )}
      </span>
    </div>
  );
}

// Fila de partida con su columna de ajuste alineada; despliega sus cuentas.
function GrupoPartidas({
  titulo, partidas, expanded, setExpanded, editable,
  ajusteDe, addAjuste, updateAjuste, removeAjuste, valorByTarget, highlight, aniosOtros,
}) {
  if (!partidas || partidas.length === 0) return null;
  return (
    <div>
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-3 mt-2 mb-1">
        {titulo}
      </h4>
      {partidas.map((p) => {
        const aj = ajusteDe(p.nombre);
        return (
          <Collapsible
            key={p.nombre}
            open={!!expanded[p.nombre]}
            onOpenChange={() => setExpanded((s) => ({ ...s, [p.nombre]: !s[p.nombre] }))}
          >
            <div
              data-ebitda-target={p.nombre}
              className={`grid grid-cols-12 gap-2 items-center py-1.5 px-3 rounded hover:bg-muted/40 transition-shadow ${
                highlight === p.nombre ? "ring-2 ring-primary" : ""
              }`}
            >
              <div className="col-span-6 flex items-center gap-1 min-w-0">
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-1 text-left min-w-0">
                    {expanded[p.nombre] ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                    <span className="text-sm truncate">{p.nombre}</span>
                  </button>
                </CollapsibleTrigger>
                {p.en_ebitda && <Badge variant="secondary" className="text-[11px] shrink-0">EBITDA</Badge>}
              </div>
              <span className={`col-span-3 text-right font-mono text-sm ${p.valor_pyg < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                {formatCurrency(p.valor_pyg)}
              </span>
              <div className="col-span-3 flex justify-end">
                <CeldaAjuste
                  scope="partida"
                  target={p.nombre}
                  enEbitda={p.en_ebitda}
                  ajuste={aj}
                  editable={editable}
                  valor={p.valor_pyg}
                  aniosOtros={aniosOtros}
                  onAdd={addAjuste}
                  onChange={(patch) => aj && updateAjuste(aj._i, patch)}
                  onRemove={() => aj && removeAjuste(aj._i)}
                />
              </div>
            </div>
            <CollapsibleContent>
              <div className="ml-5 border-l pl-2">
                {(p.cuentas || []).map((c) => {
                  const ajc = ajusteDe(c.codigo);
                  return (
                    <div
                      key={c.codigo}
                      data-ebitda-target={c.codigo}
                      className={`grid grid-cols-12 gap-2 items-center py-1 px-2 text-sm transition-shadow ${
                        highlight === c.codigo ? "ring-2 ring-primary rounded" : ""
                      }`}
                    >
                      <span className="col-span-6 truncate text-muted-foreground">
                        <span className="font-mono text-xs">{c.codigo}</span> · {c.nombre}
                      </span>
                      <span className={`col-span-3 text-right font-mono text-xs ${c.valor_pyg < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                        {formatCurrency(c.valor_pyg)}
                      </span>
                      <div className="col-span-3 flex justify-end">
                        <CeldaAjuste
                          scope="cuenta"
                          target={c.codigo}
                          enEbitda={p.en_ebitda}
                          ajuste={ajc}
                          editable={editable}
                          valor={c.valor_pyg}
                          aniosOtros={aniosOtros}
                          onAdd={addAjuste}
                          onChange={(patch) => ajc && updateAjuste(ajc._i, patch)}
                          onRemove={() => ajc && removeAjuste(ajc._i)}
                        />
                      </div>
                    </div>
                  );
                })}
                {(p.cuentas || []).length === 0 && (
                  <p className="text-xs text-muted-foreground px-2 py-1">Sin cuentas con saldo.</p>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}

// Celda de la columna "Ajuste": botón "+ ajustar" o el impacto + popover de edición.
function CeldaAjuste({ scope, target, enEbitda, ajuste, editable, valor, aniosOtros, onAdd, onChange, onRemove }) {
  if (!editable) return <span className="text-xs text-muted-foreground">—</span>;

  if (!ajuste) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-6 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => onAdd(scope, target, enEbitda)}
      >
        <Plus className="h-3 w-3 mr-1" /> ajustar
      </Button>
    );
  }

  const impacto = ajuste._impacto ?? impactoDe(ajuste, valor);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1 font-mono text-xs hover:underline">
          <span className={impacto >= 0 ? "text-emerald-600" : "text-rose-600"}>
            {impacto >= 0 ? "+" : ""}{formatCurrency(impacto)}
          </span>
          <Pencil className="h-3 w-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-2" align="end">
        <p className="text-xs font-medium truncate">{target}</p>
        <p className="text-[11px] text-muted-foreground">{resumenAjuste(ajuste)} · valor {formatCurrency(valor)}</p>
        <div className="grid grid-cols-2 gap-2">
          <select
            className="h-8 rounded border bg-background px-1 text-xs"
            value={ajuste.direccion}
            onChange={(e) => onChange({ direccion: e.target.value })}
          >
            <option value="excluir">excluir</option>
            <option value="incluir">incluir</option>
          </select>
          <select
            className="h-8 rounded border bg-background px-1 text-xs"
            value={ajuste.tipo}
            onChange={(e) => onChange({ tipo: e.target.value })}
          >
            <option value="total">total</option>
            <option value="porcentaje">% parcial</option>
            <option value="importe">importe</option>
          </select>
        </div>
        {ajuste.tipo === "porcentaje" && (
          <Input
            type="number"
            className="h-8 text-xs"
            placeholder="%"
            value={ajuste.porcentaje ?? ""}
            onChange={(e) => onChange({ porcentaje: e.target.value === "" ? null : Number(e.target.value) })}
          />
        )}
        {ajuste.tipo === "importe" && (
          <Input
            type="number"
            className="h-8 text-xs"
            placeholder="importe €"
            value={ajuste.importe ?? ""}
            onChange={(e) => onChange({ importe: e.target.value === "" ? null : Number(e.target.value) })}
          />
        )}
        <Input
          className="h-8 text-xs"
          placeholder="concepto / nota (p.ej. no recurrente)"
          value={ajuste.nota || ""}
          onChange={(e) => onChange({ nota: e.target.value })}
        />
        {scope !== "libre" && aniosOtros && aniosOtros.length > 0 && (
          <div className="pt-1">
            <p className="text-[11px] text-muted-foreground mb-1">Aplicar también a los años:</p>
            <div className="flex flex-wrap gap-1.5">
              {aniosOtros.map((anio) => {
                const sel = Array.isArray(ajuste.aplicar_en) && ajuste.aplicar_en.includes(anio);
                return (
                  <button
                    key={anio}
                    type="button"
                    onClick={() => {
                      const prev = Array.isArray(ajuste.aplicar_en) ? ajuste.aplicar_en : [];
                      const next = sel ? prev.filter((a) => a !== anio) : [...prev, anio];
                      onChange({ aplicar_en: next.length ? next.sort((a, b) => a - b) : null });
                    }}
                    className={`px-2 py-0.5 rounded text-xs font-mono border ${
                      sel ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground"
                    }`}
                  >
                    {anio}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Si ese año ya tiene un ajuste propio en esta línea, el suyo tiene prioridad.
            </p>
          </div>
        )}
        <div className="flex items-center justify-between pt-1">
          <span className={`font-mono text-xs ${impacto >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            impacto {impacto >= 0 ? "+" : ""}{formatCurrency(impacto)}
          </span>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-rose-500" onClick={onRemove}>
            <Trash2 className="h-3 w-3 mr-1" /> Quitar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Línea "Total ajustes" desplegable: al abrir, lista solo-lectura de todos los
// ajustes aplicados con su impacto, y un botón para ir a su línea en el informe.
function DesgloseAjustes({ filas, open, onToggle, totalAjustes, onGoto }) {
  return (
    <Collapsible open={open} onOpenChange={onToggle}>
      <div className="grid grid-cols-12 gap-2 items-center py-1.5 px-3">
        <CollapsibleTrigger asChild>
          <button className="col-span-6 flex items-center gap-1 text-left text-sm text-muted-foreground hover:text-foreground">
            {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
            Total ajustes de normalización
            <Badge variant="outline" className="ml-1 text-[11px]">{filas.length}</Badge>
          </button>
        </CollapsibleTrigger>
        <span className={`col-span-3 text-right font-mono text-sm ${totalAjustes < 0 ? "text-rose-600" : "text-emerald-600"}`}>
          {totalAjustes >= 0 ? "+" : ""}{formatCurrency(totalAjustes)}
        </span>
        <span className="col-span-3" />
      </div>
      <CollapsibleContent>
        <div className="ml-5 border-l pl-2 mb-2">
          {filas.length === 0 ? (
            <p className="text-xs text-muted-foreground px-2 py-1">Sin ajustes aplicados todavía.</p>
          ) : (
            filas.map(({ a, i, valor, impacto, etiqueta }) => (
              <div key={a.id || i} className="grid grid-cols-12 gap-2 items-center py-1 px-2 text-xs">
                <div className="col-span-6 min-w-0">
                  <span className="truncate block">{(a.nota || "").trim() || a.concepto || "(sin concepto)"}</span>
                  <span className="text-[11px] text-muted-foreground truncate block">
                    {etiqueta} · {resumenAjuste(a)}
                  </span>
                </div>
                <span className="col-span-2 text-right font-mono text-muted-foreground">
                  {valor == null ? "—" : formatCurrency(valor)}
                </span>
                <span className={`col-span-3 text-right font-mono ${impacto >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {impacto >= 0 ? "+" : ""}{formatCurrency(impacto)}
                </span>
                <div className="col-span-1 flex justify-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    title="Ir a la línea"
                    onClick={() => onGoto(a)}
                  >
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Fila de ajuste libre (sin cuenta detrás).
function FilaLibre({ a, impacto, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center py-1.5 px-3">
      <Input
        className="col-span-6 h-8 text-xs"
        placeholder="concepto (p.ej. saneamiento puntual)"
        value={a.nota || ""}
        onChange={(e) => onChange({ nota: e.target.value })}
      />
      <Input
        type="number"
        className="col-span-3 h-8 text-xs text-right"
        placeholder="importe €"
        value={a.importe ?? ""}
        onChange={(e) => onChange({ importe: e.target.value === "" ? null : Number(e.target.value) })}
      />
      <div className="col-span-3 flex items-center justify-end gap-2">
        <span className={`font-mono text-xs ${impacto >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
          {impacto >= 0 ? "+" : ""}{formatCurrency(impacto)}
        </span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onRemove}>
          <Trash2 className="h-4 w-4 text-rose-500" />
        </Button>
      </div>
    </div>
  );
}
